import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useProject } from '@/contexts/ProjectContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowLeft, RefreshCw, Loader2, Users, BarChart3, TrendingUp,
  Clock, DollarSign, AlertTriangle, Target, Plus, X, Save, Calendar, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { AgGridReact } from 'ag-grid-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, AreaChart, Area, ComposedChart, Line
} from 'recharts';
import ModuleInsightsDashboard from '@/components/common/ModuleInsightsDashboard';

function fmt(val) {
  if (val === undefined || val === null || val === 0) return '0';
  if (Math.abs(val) >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (Math.abs(val) >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function fmtMH(val) {
  if (val === undefined || val === null || val === 0) return '0';
  if (Math.abs(val) >= 1000) return `${(val / 1000).toFixed(1)}K`;
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

const TRADE_COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#143A6B', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16', '#06B6D4', '#D946EF', '#0EA5E9', '#F43F5E', '#22D3EE'];
const CONTRACTOR_COLORS = ['#2563EB', '#D97706', '#059669', '#DC2626', '#7C3AED', '#DB2777', '#0D9488', '#EA580C', '#4F46E5', '#65A30D', '#0891B2'];

function KPICard({ title, value, subtitle, icon: Icon, color = '#0EA5E9' }) {
  return (
    <div className="rounded-xl p-4" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
      style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="flex items-start justify-between mb-2">
        <div className="p-2 rounded-lg" style={{ background: `${color}12`, border: `1px solid ${color}20` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{value}</div>
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{title}</div>
      {subtitle && <div className="text-[10px] mt-1" style={{ color: 'var(--text-disabled)' }}>{subtitle}</div>}
    </div>
  );
}

// Daily Manpower Update Modal
function DailyUpdateModal({ projectId, trades, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    tradeCode: trades[0]?.tradeCode || '',
    contractor: trades[0]?.contractor || '',
    headcount: '',
    manhours: '',
    overtimeHours: '0',
    notes: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inputCls = "w-full rounded-lg px-3 py-2 text-sm";
  const inputStyle = { backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' };
  const labelStyle = { color: 'var(--text-muted)' };

  // Auto-fill contractor when trade changes
  const handleTradeChange = (tc) => {
    set('tradeCode', tc);
    const t = trades.find(t => t.tradeCode === tc);
    if (t?.contractor) set('contractor', t.contractor);
  };

  const handleSubmit = async () => {
    if (!form.tradeCode || !form.headcount) { toast.error('Trade and headcount are required'); return; }
    setSaving(true);
    try {
      await api.post(`/v1/projects/${projectId}/cc/manpower/daily-update`, {
        ...form, headcount: +form.headcount, manhours: +form.manhours || (+form.headcount * 10),
        overtimeHours: +form.overtimeHours || 0,
      });
      toast.success('Daily update recorded');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to record'); }
    finally { setSaving(false); }
  };

  const uniqueContractors = [...new Set(trades.map(t => t.contractor).filter(Boolean))];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 bg-black/40" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} onClick={e => e.stopPropagation()} data-testid="daily-update-modal">
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Daily Manpower Update</h2>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-xs font-medium mb-1 block" style={labelStyle}>Date *</span>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} className={inputCls} style={inputStyle} data-testid="daily-date-input" />
            </div>
            <div>
              <span className="text-xs font-medium mb-1 block" style={labelStyle}>Trade *</span>
              <select value={form.tradeCode} onChange={e => handleTradeChange(e.target.value)} className={inputCls} style={inputStyle} data-testid="daily-trade-select">
                {trades.map(t => <option key={t.tradeCode} value={t.tradeCode}>{t.tradeName}</option>)}
              </select>
            </div>
          </div>
          <div>
            <span className="text-xs font-medium mb-1 block" style={labelStyle}>Contractor</span>
            <select value={form.contractor} onChange={e => set('contractor', e.target.value)} className={inputCls} style={inputStyle} data-testid="daily-contractor-select">
              <option value="">— Select —</option>
              {uniqueContractors.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <span className="text-xs font-medium mb-1 block" style={labelStyle}>Headcount *</span>
              <input type="number" value={form.headcount} onChange={e => set('headcount', e.target.value)} className={inputCls} style={inputStyle} placeholder="0" data-testid="daily-headcount-input" />
            </div>
            <div>
              <span className="text-xs font-medium mb-1 block" style={labelStyle}>Manhours</span>
              <input type="number" value={form.manhours} onChange={e => set('manhours', e.target.value)} className={inputCls} style={inputStyle} placeholder="Auto: HC×10" data-testid="daily-manhours-input" />
            </div>
            <div>
              <span className="text-xs font-medium mb-1 block" style={labelStyle}>OT Hours</span>
              <input type="number" value={form.overtimeHours} onChange={e => set('overtimeHours', e.target.value)} className={inputCls} style={inputStyle} placeholder="0" data-testid="daily-ot-input" />
            </div>
          </div>
          <div>
            <span className="text-xs font-medium mb-1 block" style={labelStyle}>Notes</span>
            <input value={form.notes} onChange={e => set('notes', e.target.value)} className={inputCls} style={inputStyle} placeholder="Optional remarks" data-testid="daily-notes-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid="daily-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Record
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SwmCognaraPlanTab({ projectId }) {
  const [rows, setRows] = useState({ manpower: [], equipment: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.get(`/v1/projects/${projectId}/swm/cognara/manpower-equipment/plan`)
      .then(r => setRows({ manpower: r.data.manpower || [], equipment: r.data.equipment || [] }))
      .catch(() => setRows({ manpower: [], equipment: [] }))
      .finally(() => setLoading(false));
  }, [projectId]);

  const totalMp = rows.manpower.reduce((s, r) => s + (r.headcount || 0), 0);
  const totalEq = rows.equipment.reduce((s, r) => s + (r.qty || 0), 0);

  // Group manpower rows by tag → mark for readable display
  const grouped = useMemo(() => {
    const g = {};
    rows.manpower.forEach(r => {
      const k = `${r.structuralTag}|${r.markNumber}`;
      if (!g[k]) g[k] = { structuralTag: r.structuralTag, markNumber: r.markNumber, manpower: [], equipment: [] };
      g[k].manpower.push(r);
    });
    rows.equipment.forEach(r => {
      const k = `${r.structuralTag}|${r.markNumber}`;
      if (!g[k]) g[k] = { structuralTag: r.structuralTag, markNumber: r.markNumber, manpower: [], equipment: [] };
      g[k].equipment.push(r);
    });
    return Object.values(g).sort((a, b) => (a.structuralTag + a.markNumber).localeCompare(b.structuralTag + b.markNumber));
  }, [rows]);

  if (loading) return <div className="rounded-xl p-8 text-center text-sm" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-muted)' }}><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading Cognara SWM plan…</div>;
  if (grouped.length === 0) return <div className="rounded-xl p-8 text-center text-sm" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>No SWM plan pushed yet. Go to Structural Workfront Management → select tags → "Cognara: MP & Equipment" → approve → push.</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
        <span className="px-2 py-1 rounded font-mono font-semibold" style={{ background: '#22C55E22', color: '#16A34A' }}>{totalMp} total headcount</span>
        <span className="px-2 py-1 rounded font-mono font-semibold" style={{ background: '#6366F122', color: '#6366F1' }}>{totalEq} total equipment units</span>
        <span>· covering {grouped.length} tag→mark plans</span>
      </div>
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        <table className="w-full text-xs" data-testid="swm-cognara-plan-table">
          <thead style={{ background: 'var(--card-bg-alt)' }}>
            <tr>
              <th className="text-left p-2">Tag → Mark</th>
              <th className="text-left p-2">Window</th>
              <th className="text-left p-2">Fabrication</th>
              <th className="text-left p-2">Erection</th>
            </tr>
          </thead>
          <tbody>
            {grouped.map((g, i) => (
              <tr key={i} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                <td className="p-2 align-top">
                  <div className="font-mono text-[10px] font-semibold" style={{ color: '#0EA5E9' }}>{g.structuralTag}</div>
                  <div className="font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}>{g.markNumber}</div>
                </td>
                <td className="p-2 align-top text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  {(g.manpower[0]?.startDate || g.equipment[0]?.startDate) ? (
                    <>
                      <div>Start {new Date(g.manpower[0]?.startDate || g.equipment[0]?.startDate).toLocaleDateString()}</div>
                      <div>End {new Date(g.manpower[0]?.endDate || g.equipment[0]?.endDate).toLocaleDateString()}</div>
                    </>
                  ) : '—'}
                </td>
                <td className="p-2 align-top">
                  <div className="flex flex-wrap gap-1">
                    {g.manpower.filter(m => m.phase === 'FAB').map((m, k) => (
                      <span key={k} className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: '#22C55E22', color: '#16A34A' }}>{m.headcount} {m.trade}</span>
                    ))}
                    {g.equipment.filter(m => m.phase === 'FAB').map((m, k) => (
                      <span key={k} className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: '#6366F122', color: '#6366F1' }}>{m.qty} {m.category}</span>
                    ))}
                  </div>
                </td>
                <td className="p-2 align-top">
                  <div className="flex flex-wrap gap-1">
                    {g.manpower.filter(m => m.phase === 'ERECTION').map((m, k) => (
                      <span key={k} className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: '#F5920622', color: '#F59206' }}>{m.headcount} {m.trade}</span>
                    ))}
                    {g.equipment.filter(m => m.phase === 'ERECTION').map((m, k) => (
                      <span key={k} className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: '#6366F122', color: '#6366F1' }}>{m.qty} {m.category}{m.capacityTon ? ` (${m.capacityTon} t)` : ''}</span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default function ManpowerManagement() {
  const { projectId: ctxProjectId } = useProject();
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [manpowerData, setManpowerData] = useState([]);
  const [histogramData, setHistogramData] = useState(null);
  const [productivityData, setProductivityData] = useState([]);
  const [sCurveData, setSCurveData] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('histogram');
  const [histView, setHistView] = useState('trade'); // 'trade' or 'contractor'
  const [showDailyUpdate, setShowDailyUpdate] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await api.get('/v1/cc/projects-with-data');
      setProjects(data.projects || []);
      if (data.projects?.length > 0 && !selectedProject) setSelectedProject(data.projects[0].project_id);
    } catch { toast.error('Failed to load projects'); }
  }, [selectedProject]);

  const fetchAll = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const [mp, hist, prod, sc] = await Promise.all([
        api.get(`/v1/projects/${selectedProject}/cc/manpower`),
        api.get(`/v1/projects/${selectedProject}/cc/manpower/histogram`),
        api.get(`/v1/projects/${selectedProject}/cc/manpower/productivity`),
        api.get(`/v1/projects/${selectedProject}/cc/cbs/analytics/manpower-s-curve`),
      ]);
      setManpowerData(mp.data.manpower || []);
      setHistogramData(hist.data);
      setProductivityData(prod.data.productivity || []);
      setSCurveData(sc.data.curve || []);
    } catch { toast.error('Failed to load manpower data'); }
    finally { setLoading(false); }
  }, [selectedProject]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { if (selectedProject) fetchAll(); }, [selectedProject, fetchAll]);

  // Summary stats
  const totalPlannedMH = manpowerData.reduce((s, m) => s + (m.planned?.totalManhours || 0), 0);
  const totalActualMH = manpowerData.reduce((s, m) => s + (m.actual?.totalManhours || 0), 0);
  const totalActualCost = manpowerData.reduce((s, m) => s + (m.actual?.totalCost || 0), 0);
  const totalHeadcount = manpowerData.reduce((s, m) => s + (m.actual?.currentHeadcount || 0), 0);
  const peakPlanned = manpowerData.reduce((s, m) => s + (m.planned?.peakManpower || 0), 0);

  // Build trade color map
  const tradeColorMap = useMemo(() => {
    const m = {};
    (histogramData?.trades || []).forEach((t, i) => { m[t.tradeCode] = TRADE_COLORS[i % TRADE_COLORS.length]; });
    return m;
  }, [histogramData]);

  // Build contractor color map
  const contractorColorMap = useMemo(() => {
    const cList = [...new Set((histogramData?.trades || []).map(t => t.contractor).filter(Boolean))];
    const m = {};
    cList.forEach((c, i) => { m[c] = CONTRACTOR_COLORS[i % CONTRACTOR_COLORS.length]; });
    m['Unassigned'] = '#94A3B8';
    return m;
  }, [histogramData]);

  // Histogram chart data — by trade
  const histByTrade = useMemo(() => {
    if (!histogramData?.periods) return [];
    return histogramData.periods.map(p => {
      const row = { label: p.periodId.slice(2) };
      let totalPlanned = 0, totalActual = 0;
      for (const t of (histogramData.trades || [])) {
        row[`p_${t.tradeCode}`] = (p.planned || {})[t.tradeCode] || 0;
        row[`a_${t.tradeCode}`] = (p.actual || {})[t.tradeCode] || 0;
        totalPlanned += (p.planned || {})[t.tradeCode] || 0;
        totalActual += (p.actual || {})[t.tradeCode] || 0;
      }
      row.totalPlanned = totalPlanned;
      row.totalActual = totalActual;
      return row;
    });
  }, [histogramData]);

  // Histogram chart data — by contractor
  const histByContractor = useMemo(() => {
    if (!histogramData?.periods) return [];
    // Build contractor→tradeCodes map
    const cTradeMap = {};
    for (const t of (histogramData.trades || [])) {
      const c = t.contractor || 'Unassigned';
      if (!cTradeMap[c]) cTradeMap[c] = [];
      cTradeMap[c].push(t.tradeCode);
    }
    return histogramData.periods.map(p => {
      const row = { label: p.periodId.slice(2) };
      let totalPlanned = 0, totalActual = 0;
      for (const [c, codes] of Object.entries(cTradeMap)) {
        let cp = 0, ca = 0;
        for (const tc of codes) {
          cp += (p.planned || {})[tc] || 0;
          ca += (p.actual || {})[tc] || 0;
        }
        row[`p_${c}`] = cp;
        row[`a_${c}`] = ca;
        totalPlanned += cp;
        totalActual += ca;
      }
      row.totalPlanned = totalPlanned;
      row.totalActual = totalActual;
      return row;
    });
  }, [histogramData]);

  const contractorNames = useMemo(() => {
    const cSet = new Set();
    (histogramData?.trades || []).forEach(t => cSet.add(t.contractor || 'Unassigned'));
    return [...cSet];
  }, [histogramData]);

  // AG Grid columns for trade register (editable)
  const tradeColumnDefs = useMemo(() => [
    { headerName: 'Trade', field: 'tradeName', width: 170, pinned: 'left' },
    { headerName: 'Contractor', field: 'contractor', width: 140, editable: true },
    { headerName: 'Category', field: 'tradeCategory', width: 120 },
    { headerName: 'Discipline', field: 'discipline', width: 90 },
    { headerName: 'Rate/MH', field: 'rate', width: 100, type: 'numericColumn', editable: true,
      valueFormatter: p => `₹${(p.value || 0).toLocaleString('en-IN')}`, valueParser: p => Number(p.newValue) },
    { headerName: 'Planned MH', field: 'plannedMH', width: 110, type: 'numericColumn', editable: true,
      valueFormatter: p => fmtMH(p.value), valueParser: p => Number(p.newValue) },
    { headerName: 'Actual MH', field: 'actualMH', width: 110, type: 'numericColumn', editable: true,
      valueFormatter: p => fmtMH(p.value), valueParser: p => Number(p.newValue) },
    { headerName: 'Variance MH', field: 'varianceMH', width: 110, type: 'numericColumn', valueFormatter: p => fmtMH(p.value),
      cellStyle: p => ({ color: (p.value || 0) >= 0 ? '#22C55E' : '#EF4444', fontWeight: '600' }) },
    { headerName: 'Planned Cost', field: 'plannedCost', width: 120, type: 'numericColumn', valueFormatter: p => fmt(p.value) },
    { headerName: 'Actual Cost', field: 'actualCost', width: 120, type: 'numericColumn', valueFormatter: p => fmt(p.value) },
    { headerName: 'Variance Cost', field: 'varianceCost', width: 120, type: 'numericColumn', valueFormatter: p => fmt(p.value),
      cellStyle: p => ({ color: (p.value || 0) >= 0 ? '#22C55E' : '#EF4444', fontWeight: '600' }) },
    { headerName: 'Productivity', field: 'productivityIndex', width: 110, type: 'numericColumn',
      valueFormatter: p => p.value != null ? p.value.toFixed(3) : '—',
      cellStyle: p => ({ color: (p.value || 0) >= 1 ? '#22C55E' : '#EF4444', fontWeight: '600' }) },
  ], []);

  // Handle cell edit
  const onCellValueChanged = useCallback(async (params) => {
    const { data, colDef } = params;
    const field = colDef.field;
    const updates = {};
    if (field === 'contractor') updates.contractor = data.contractor;
    if (field === 'rate') updates.standardRate = data.rate;
    if (field === 'plannedMH') updates.totalPlannedManhours = data.plannedMH;
    if (field === 'actualMH') {
      // Recalculate variance locally
      data.varianceMH = (data.plannedMH || 0) - data.actualMH;
      params.api.refreshCells({ rowNodes: [params.node], columns: ['varianceMH'] });
    }
    try {
      await api.put(`/v1/projects/${selectedProject}/cc/manpower/${data.tradeCode}`, updates);
      toast.success(`${data.tradeName}: ${field} updated`);
    } catch { toast.error('Update failed'); }
  }, [selectedProject]);

  return (
    <DashboardLayout>
      {ctxProjectId && <AIAnalystPanel projectId={ctxProjectId} moduleKey="MANPOWER_MGMT" defaultOpen={true} compact={true} />}

      <div className="p-6 space-y-5" data-testid="manpower-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="p-2.5 rounded-xl" style={{ background: '#3B82F612', border: '1px solid #3B82F620' }}>
              <Users className="w-5 h-5" style={{ color: '#3B82F6' }} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Manpower Management</h1>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Trade register, manhour tracking, productivity metrics</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select className="text-sm rounded-lg px-3 py-1.5"
              style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
              value={selectedProject || ''} onChange={e => setSelectedProject(e.target.value)} data-testid="project-selector">
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
            </select>
            <Button size="sm" onClick={() => setShowDailyUpdate(true)} data-testid="daily-update-btn">
              <Calendar className="w-4 h-4 mr-1" /> Daily Update
            </Button>
            <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} />
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-5 gap-3">
              <KPICard title="Current Headcount" value={totalHeadcount.toLocaleString('en-IN')} subtitle={`Peak planned: ${peakPlanned}`} icon={Users} color="#3B82F6" />
              <KPICard title="Planned Manhours" value={fmtMH(totalPlannedMH)} icon={Clock} color="#143A6B" />
              <KPICard title="Actual Manhours" value={fmtMH(totalActualMH)} icon={Target} color="#F59E0B" />
              <KPICard title="MH Variance" value={fmtMH(totalPlannedMH - totalActualMH)}
                icon={totalPlannedMH - totalActualMH >= 0 ? TrendingUp : AlertTriangle}
                color={totalPlannedMH - totalActualMH >= 0 ? '#22C55E' : '#EF4444'} />
              <KPICard title="Total Labour Cost" value={fmt(totalActualCost)} icon={DollarSign} color="#10B981" />
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-0.5 rounded-lg" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
              {[
                { id: 'histogram', label: 'Manpower Histogram', icon: BarChart3 },
                { id: 'insights', label: 'AI Insights', icon: Sparkles },
                { id: 'scurve', label: 'Manhour S-Curve', icon: TrendingUp },
                { id: 'register', label: 'Trade Register', icon: Users },
                { id: 'swm-plan', label: 'Cognara SWM Plan', icon: Sparkles },
              ].map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-md transition-all"
                  style={{
                    backgroundColor: activeTab === t.id ? '#3B82F615' : 'transparent',
                    color: activeTab === t.id ? '#3B82F6' : 'var(--text-muted)',
                    fontWeight: activeTab === t.id ? '600' : '400'
                  }} data-testid={`tab-${t.id}`}>
                  <t.icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              ))}
            </div>

            {/* HISTOGRAM TAB */}
            {activeTab === 'insights' && (
              <ModuleInsightsDashboard projectId={selectedProject} moduleKey="manpower"
                color="#3B82F6" deepLinkBase="/project-controls/cost/manpower" />
            )}
            {activeTab === 'histogram' && (
              <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Planned vs Actual Headcount
                  </h3>
                  <div className="flex gap-1 p-0.5 rounded-lg" style={{ backgroundColor: 'var(--hover-bg)', border: '1px solid var(--border-color)' }}>
                    {[{ id: 'trade', label: 'By Trade' }, { id: 'contractor', label: 'By Contractor' }].map(v => (
                      <button key={v.id} onClick={() => setHistView(v.id)}
                        className="px-3 py-1 text-xs rounded-md transition-all"
                        style={{
                          backgroundColor: histView === v.id ? '#3B82F615' : 'transparent',
                          color: histView === v.id ? '#3B82F6' : 'var(--text-muted)',
                          fontWeight: histView === v.id ? '600' : '400'
                        }} data-testid={`hist-view-${v.id}`}>{v.label}</button>
                    ))}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={histView === 'trade' ? histByTrade : histByContractor} margin={{ bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {histView === 'trade' ? (
                      (histogramData?.trades || []).map(t => (
                        <Bar key={`p_${t.tradeCode}`} dataKey={`p_${t.tradeCode}`} stackId="planned" name={`${t.tradeName} (P)`}
                          fill={tradeColorMap[t.tradeCode] || '#6B7280'} opacity={0.7} />
                      ))
                    ) : (
                      contractorNames.map(c => (
                        <Bar key={`p_${c}`} dataKey={`p_${c}`} stackId="planned" name={`${c} (P)`}
                          fill={contractorColorMap[c] || '#94A3B8'} opacity={0.7} />
                      ))
                    )}
                    <Line type="monotone" dataKey="totalActual" name="Actual Total" stroke="#EF4444" strokeWidth={2.5} dot={false} />
                    <Line type="monotone" dataKey="totalPlanned" name="Planned Total" stroke="#0EA5E9" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* S-CURVE TAB */}
            {activeTab === 'scurve' && (
              <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                  Cumulative Manhour S-Curve
                </h3>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={sCurveData} margin={{ bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="periodId" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                    <YAxis tickFormatter={fmtMH} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                    <Tooltip formatter={v => fmtMH(v)} contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', fontSize: 11 }} />
                    <Legend />
                    <Area type="monotone" dataKey="cumPlannedMH" name="Planned (Cum)" stroke="#3B82F6" fill="#3B82F620" strokeWidth={2} />
                    <Area type="monotone" dataKey="cumActualMH" name="Actual (Cum)" stroke="#EF4444" fill="#EF444420" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* TRADE REGISTER TAB (Editable) */}
            {activeTab === 'register' && (
              <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }} data-testid="manpower-grid">
                  <AgGridReact
                    ref={gridRef}
                    columnDefs={tradeColumnDefs}
                    rowData={productivityData}
                    defaultColDef={{
                      sortable: true, resizable: true, cellStyle: { fontSize: '12px' },
                      suppressMenu: false,
                    }}
                    onCellValueChanged={onCellValueChanged}
                    animateRows={true}
                    headerHeight={36}
                    rowHeight={34}
                    singleClickEdit={true}
                    stopEditingWhenCellsLoseFocus={true}
                  />
                </div>
              </div>
            )}
            {activeTab === 'swm-plan' && (
              <SwmCognaraPlanTab projectId={selectedProject} />
            )}
          </>
        )}

        {/* Daily Update Modal */}
        {showDailyUpdate && selectedProject && (
          <DailyUpdateModal
            projectId={selectedProject}
            trades={manpowerData.map(m => ({ tradeCode: m.tradeCode, tradeName: m.tradeName, contractor: m.contractor || '' }))}
            onClose={() => setShowDailyUpdate(false)}
            onSaved={() => { setShowDailyUpdate(false); fetchAll(); }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
